// Màn tài khoản: xem thông tin, đổi mật khẩu, đăng xuất.
//
// Mở bằng cách bấm dòng "Chào <email>" ở đầu màn Trang chủ — không dựng
// thanh menu riêng cho một màn dùng thỉnh thoảng.
//
// Đổi mật khẩu ở đây BẮT BUỘC có mật khẩu cũ. Không bắt thì kẻ đã đánh cắp
// cookie phiên cũng đổi được rồi khoá chính chủ nhân ra — tức chiếm đoạt vĩnh
// viễn. Server cũng tự đăng xuất mọi máy khác, nên phải NÓI rõ đã đuổi được
// bao nhiêu máy: im lặng thì người dùng tưởng máy khác vẫn an toàn.

import { useState } from 'react'
import { api } from '../api'
import type { User } from '../types'
import { ApiError } from '../types'
import { Label, Note, Screen, Shell } from '../components/shell'
import { TopBar } from '../components/ui'
import { Icon } from '../components/Icon'

export function AccountView({
  user,
  onBack,
  onLoggedOut,
}: {
  user: User | null
  onBack: () => void
  onLoggedOut: () => void
}) {
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')

  /** Ô "xác nhận mật khẩu mới" bắt buộc.
   *
   *  Đây không phải thủ tục hành chính: gõ sai ở ô mật khẩu mới rồi bấm nút là
   *  khóa chính mình ra khỏi tài khoản, mà app này CHƯA có đường reset bằng
   *  email. Gõ lại là cách rẻ nhất chặn việc đó. */
  const mismatch = again.length > 0 && again !== next

  async function submit() {
    setErr('')
    setDone('')
    if (mismatch) {
      setErr('Hai mật khẩu mới không khớp.')
      return
    }
    setBusy(true)
    try {
      const r = await api.changePassword(cur, next)
      setCur('')
      setNext('')
      setAgain('')
      // Nói rõ đã đuổi bao nhiêu máy. Đây là thứ người dùng cần biết nhất sau
      // khi đổi mật khẩu.
      setDone(
        r.other_sessions_gone === 0
          ? 'Đã đổi mật khẩu. Máy này vẫn đang đăng nhập.'
          : `Đã đổi mật khẩu và đăng xuất ${r.other_sessions_gone} máy khác.`,
      )
    } catch (e) {
      const a = e as ApiError
      // `no_password` là tình huống vĩnh viễn của tài khoản OAuth, không phải
      // lỗi gõ — nên ẩn luôn cả form thay vì để người dùng thử tới lui.
      if (a.code === 'no_password') setErr(a.detail)
      else if (a.code === 'offline') setErr('Không có mạng. Cần mạng để đổi mật khẩu.')
      else setErr(a.detail || 'Không đổi được mật khẩu.')
    } finally {
      setBusy(false)
    }
  }

  async function logout() {
    setBusy(true)
    try {
      await api.logout()
    } catch {
      // Đăng xuất hỏng mạng thì vẫn dẫn về màn đăng nhập: cookie sẽ hết hạn
      // hoặc người dùng xoá tay. Giữ người dùng lại màn này thì họ bấm lại được
      // mà chẳng có gì đổi.
    }
    onLoggedOut()
  }

  const noPwd = user?.has_password === false

  return (
    <Shell>
      <Screen>
        <TopBar title="Tài khoản" onBack={onBack} />

        <Label className="mb-1.5">Đang đăng nhập</Label>
        <div className="card">
          <div className="font-medium break-all">{user?.email}</div>
          <Note className="mt-1">
            {user?.full_library
              ? 'Có toàn bộ kho câu hỏi.'
              : 'Đang dùng kho câu hỏi mẫu (200 câu).'}
          </Note>
        </div>

        <Label className="mt-6 mb-1.5">Mật khẩu</Label>
        {noPwd ? (
          // Tài khoản OAuth không có mật khẩu để đổi. Hiện lời giải thích thay vì
          // form vô dụng — form mà bấm nào cũng lỗi thì tệ hơn không có.
          <Note>
            Tài khoản này đăng nhập bằng Google/Facebook nên không có mật khẩu. Muốn
            thêm mật khẩu thì cần đăng nhập bằng email trước — chưa hỗ trợ.
          </Note>
        ) : (
          <>
            {done && (
              <div className="mb-3 rounded-lg border border-good/30 bg-good-soft px-3 py-2 text-sm text-good">
                {done}
              </div>
            )}
            {err && (
              <div className="mb-3 rounded-lg border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
                {err}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm" htmlFor="cp-cur">
                  Mật khẩu hiện tại
                </label>
                <input
                  id="cp-cur"
                  type={show ? 'text' : 'password'}
                  className="input"
                  value={cur}
                  autoComplete="current-password"
                  onChange={(e) => setCur(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="cp-new">
                  Mật khẩu mới
                </label>
                <input
                  id="cp-new"
                  type={show ? 'text' : 'password'}
                  className="input"
                  value={next}
                  autoComplete="new-password"
                  onChange={(e) => setNext(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="cp-again">
                  Nhập lại mật khẩu mới
                </label>
                <input
                  id="cp-again"
                  type={show ? 'text' : 'password'}
                  className="input"
                  value={again}
                  autoComplete="new-password"
                  aria-invalid={mismatch}
                  onChange={(e) => setAgain(e.target.value)}
                />
                {mismatch && (
                  <Note className="mt-1 text-bad">Hai mật khẩu mới không khớp.</Note>
                )}
              </div>
            </div>

            <button
              onClick={() => setShow((v) => !v)}
              className="mt-2 cursor-pointer text-sm text-accent"
            >
              {show ? 'Ẩn' : 'Hiện'} mật khẩu
            </button>

            {/* Báo trước hậu quả ngay trên nút: người dùng phải biết mình sắp
                đá các máy khác ra, không phải tự phát hiện sau. */}
            <button
              onClick={submit}
              disabled={busy || cur === '' || next === '' || mismatch}
              className="btn btn-primary mt-3"
            >
              Đổi mật khẩu
              <Icon name="arrow" size={18} />
            </button>
            <Note className="mt-2">
              Đổi xong, mọi máy khác đang đăng nhập tài khoản này sẽ bị đăng xuất.
              Máy này vẫn giữ phiên.
            </Note>
          </>
        )}

        <Label className="mt-6 mb-1.5">Phiên</Label>
        <button onClick={logout} disabled={busy} className="btn btn-ghost border border-ink/15">
          Đăng xuất máy này
        </button>
        <Note className="mt-2">
          Máy khác vẫn đang đăng nhập. Dùng "Đổi mật khẩu" ở trên nếu muốn đuổi hết.
        </Note>
      </Screen>
    </Shell>
  )
}
